import { createApi } from '@reduxjs/toolkit/query/react';

import { adaptOddsBreakdowns } from '@api/adapters/oddsBreakdown';
import { adaptOddsFinal } from '@api/adapters/oddsFinal';
import { OddsFinal } from '@api/types/oddsFinal';
import { Lang } from '@assets/locales';
import { TAG_ODDS, TAG_RACE } from '@config/tags';
import { ODDS_FINAL, ODDS_BREAKDOWN } from '@config/urls';

import { baseQuery } from './baseQuery';

import type { BetShortCode, OddsBreakdown } from '../types';
import type { HdeSchemas } from '../types/generated/apiSchemas';

export const oddsApi = createApi({
  reducerPath: 'oddsApi',
  tagTypes: [TAG_RACE, TAG_ODDS],
  baseQuery,
  refetchOnFocus: true,
  endpoints: builder => ({
    getTemporaryOdds: builder.query<
      OddsBreakdown[],
      {
        date: string;
        racecourseCode?: string;
        raceNumber?: number;
        betShortCode: BetShortCode;
        language: Lang;
      }
    >({
      query: ({ date, racecourseCode, raceNumber, betShortCode, language }) => ({
        url: `${ODDS_BREAKDOWN}/${date}/${racecourseCode}/${raceNumber}/${betShortCode}`,
        params: { language },
      }),
      transformResponse: (response: HdeSchemas['Odds'][]): OddsBreakdown[] =>
        adaptOddsBreakdowns(response),
    }),
    getFinalDividends: builder.query<
      OddsFinal,
      {
        date: string;
        racecourseCode: string;
        raceNumber: number;
        betShortCode: BetShortCode;
        language: Lang;
      }
    >({
      query: ({ date, racecourseCode, raceNumber, betShortCode, language }) => ({
        url: `${ODDS_FINAL}/${date}/${racecourseCode}/${raceNumber}/${betShortCode}`,
        params: { language },
      }),
      transformResponse: (response: HdeSchemas['FinalDividend']): OddsFinal =>
        adaptOddsFinal(response),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetTemporaryOddsQuery, useGetFinalDividendsQuery } = oddsApi;
